import React, { useMemo, useState } from 'react';
import { Row, Col, Button, Checkbox, Empty, List, Spin, Typography } from 'antd';
import { AiFillCaretDown } from 'react-icons/ai';
import FolderSelectorTree from '../../../shared/components/FolderSelectorTree';
import SelectFileType from './SelectFileType';
import { FileTypes } from '../../../enums/fileTypes';
import { useLegendForm } from '../../../context/MapLegendContext';
import { FileNode, CustomFileMap } from '../../../types/FileManagerTypes';
import './MapViewSteps.scss';

export interface DataPortalStepProps {
  handleFileTypeChange: (v: FileTypes) => void;
  fileMap: CustomFileMap;
  selectedFiles: FileNode[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileNode[]>>;
  showFiles?: boolean;
  /** Selected folder, falling back to root once the listing resolves. */
  destinationFolderId: string;
  destinationLabel: string;
  isFolderMappingLoading: boolean;
}

const VECTOR_EXTENSIONS: Record<FileTypes, string[]> = {
  [FileTypes.SHAPEFILE]: ['.shp', '.shx', '.dbf', '.prj'],
  [FileTypes.GEOJSON]: ['.geojson', '.json'],
  [FileTypes.KML]: ['.kml'],
  [FileTypes.KMZ]: ['.kmz'],
  [FileTypes.RASTER]: [],
  [FileTypes.WMS]: [],
};

const extensionOf = (file: FileNode) => {
  const name = file.name.toLowerCase();
  return file.ext?.toLowerCase() || name.slice(name.lastIndexOf('.'));
};

const DataPortalStep: React.FC<DataPortalStepProps> = ({
  handleFileTypeChange,
  fileMap,
  selectedFiles,
  setSelectedFiles,
  showFiles = true,
  destinationFolderId,
  destinationLabel,
  isFolderMappingLoading,
}) => {
  const [isVFSVisible, setIsVFSVisible] = useState<boolean>(false);
  const { uploadFileType, setFolderLocation, setFolderId, setFolderS3Key } = useLegendForm();
  const availableFiles = useMemo(() => {
    const extensions = VECTOR_EXTENSIONS[uploadFileType] ?? [];
    return Object.values(fileMap).filter((file): file is FileNode =>
      Boolean(file && !file.isDir && file.parentId === destinationFolderId && extensions.includes(extensionOf(file))),
    );
  }, [fileMap, destinationFolderId, uploadFileType]);
  const selectedIds = new Set(selectedFiles.map((file) => file.id));

  const toggleFile = (file: FileNode, checked: boolean) => {
    setSelectedFiles((current) =>
      checked ? [...current.filter((item) => item.id !== file.id), file] : current.filter((item) => item.id !== file.id),
    );
  };

  return (
    <Row className="data-portal-modal-body" gutter={[12, 12]} align={'middle'}>
      <Col span={4}>Location:</Col>
      <Col span={20}>
        <Button onClick={() => setIsVFSVisible(true)} className="data-portal__location-btn">
          <p className="data-portal__location-text">{destinationLabel || 'Location'}</p>
          {isFolderMappingLoading ? <Spin size="small" /> : <AiFillCaretDown className="data-portal__chevron" />}
        </Button>
      </Col>
      <Col span={4}>FileType:</Col>
      <Col span={20}>
        <SelectFileType handleFileTypeChange={handleFileTypeChange} />
      </Col>
      {showFiles && (
        <>
          <Col span={4}>Files:</Col>
          <Col span={20}>
            {availableFiles.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No vector files in this folder" />
            ) : (
              <List
                size="small"
                bordered
                className="data-portal__file-list"
                dataSource={availableFiles}
                renderItem={(file) => (
                  <List.Item>
                    <Checkbox checked={selectedIds.has(file.id)} onChange={(event) => toggleFile(file, event.target.checked)}>
                      <Typography.Text className="data-portal__file-name" ellipsis={{ tooltip: file.name }}>
                        {file.name}
                      </Typography.Text>
                    </Checkbox>
                  </List.Item>
                )}
              />
            )}
          </Col>
        </>
      )}

      <FolderSelectorTree
        open={isVFSVisible}
        onClose={() => setIsVFSVisible(false)}
        variant="light"
        primaryLabel="SELECT"
        initialFolderId={destinationFolderId}
        onSelect={(f) => {
          setFolderLocation(f.name);
          setFolderId(f.id);
          setFolderS3Key(f.s3Key);
          setSelectedFiles([]);
          setIsVFSVisible(false);
        }}
      />
    </Row>
  );
};

export default DataPortalStep;
